import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Rating,
  Button,
  Grid,
  Paper,
  Avatar,
  IconButton,
  Tooltip
} from "@mui/material";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SortIcon from '@mui/icons-material/Sort';
import API_URL from "../../apiConfig";

const AdminReviewPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${API_URL}/api/reviews`);
        if (response.ok) {
          const data = await response.json();
          setReviews(data);
        }
      } catch (error) {
        console.error("Error fetching reviews:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const handleToggleVisibility = async (id, currentVisibility) => {
    try {
      const response = await fetch(
        `${API_URL}/api/reviews/${id}/toggle`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ hidden: !currentVisibility }),
        }
      );

      if (response.ok) {
        setReviews((prevReviews) =>
          prevReviews.map((review) =>
            review._id === id
              ? { ...review, hidden: !currentVisibility }
              : review
          )
        );
      }
    } catch (error) {
      console.error("Error updating review visibility:", error);
    }
  };

  const handleDeleteReview = async (id) => {
    if (window.confirm("Are you sure you want to delete this review?")) {
      try {
        const response = await fetch(`${API_URL}/api/reviews/${id}`, { method: "DELETE" });
        if (response.ok) {
          setReviews((prevReviews) => prevReviews.filter((review) => review._id !== id));
        }
      } catch (error) {
        console.error("Error deleting review:", error);
      }
    }
  };

  const sortByPostedTime = () => {
    setReviews((prevReviews) =>
      [...prevReviews].sort((a, b) => new Date(b.submissionTime) - new Date(a.submissionTime))
    );
  };

  if (loading) {
    return <Typography sx={{ color: '#e88ba9' }}>Loading reviews...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontFamily: '"Playfair Display", serif', color: '#d46a89' }}>
          Customer Reviews
        </Typography>

        <Button
          startIcon={<SortIcon />}
          variant="outlined"
          onClick={sortByPostedTime}
          sx={{ borderRadius: '20px', color: '#d46a89', borderColor: '#e88ba9', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#fcf4f4', borderColor: '#d46a89' } }}
        >
          Sort by Newest
        </Button>
      </Box>

      {reviews.length === 0 ? (
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', borderRadius: '24px', border: '1px dashed #f4a2bc', background: 'transparent' }}>
          <Typography sx={{ color: '#8c7077', fontSize: '1.1rem' }}>No reviews have been written yet.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {reviews.map((review) => (
            <Grid item xs={12} md={6} lg={4} key={review._id}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: '24px',
                  border: '1px solid',
                  borderColor: review.hidden ? '#e0e0e0' : 'rgba(232, 139, 169, 0.3)',
                  background: review.hidden ? '#f9f9f9' : '#fff',
                  opacity: review.hidden ? 0.7 : 1,
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 30px rgba(232, 139, 169, 0.1)'
                  }
                }}
              >
                {/* Header section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: review.hidden ? '#bdbdbd' : '#e88ba9', width: 36, height: 36, fontSize: '1rem' }}>
                      {review.userName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#5c434a', lineHeight: 1.2 }}>{review.userName}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#8c7077' }}>
                        {new Date(review.submissionTime).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Actions */}
                  <Box>
                    <Tooltip title={review.hidden ? "Unhide Review" : "Hide from Website"}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleVisibility(review._id, review.hidden)}
                        sx={{ color: review.hidden ? '#9e9e9e' : '#d46a89' }}
                      >
                        {review.hidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Permanently">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteReview(review._id)}
                        sx={{ color: '#e53935' }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Ratings */}
                <Box sx={{ mb: 2, p: 1.5, background: '#fffafb', borderRadius: '12px' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#8c7077', mb: 0.5 }}>Item: {review.itemCode}</Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography fontSize="0.8rem" color="textSecondary">Taste</Typography>
                    <Rating value={review.tasteRating} readOnly size="small" />
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography fontSize="0.8rem" color="textSecondary">Looks</Typography>
                    <Rating value={review.lookRating} readOnly size="small" />
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography fontSize="0.8rem" color="textSecondary">Value</Typography>
                    <Rating value={review.valueRating} readOnly size="small" />
                  </Box>
                </Box>

                {/* Comment Body */}
                <Typography sx={{ color: '#5c434a', fontStyle: 'italic', flexGrow: 1 }}>
                  "{review.comment}"
                </Typography>

              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default AdminReviewPage;
